import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppLayout } from './components/shared/AppLayout';
import { PublicLayout } from './components/shared/PublicLayout';
import { PageLoader } from './components/shared/PageLoader';
import { useAuthStore } from './store/auth';
import { usePageTracking } from './analytics';

const LandingPage = lazy(() => import('./pages/Landing'));
const LoginPage = lazy(() => import('./pages/Login'));
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const AgentPage = lazy(() => import('./pages/Agent'));
const OnboardPage = lazy(() => import('./pages/Onboard'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const ProfileSetupPage = lazy(() => import('./pages/ProfileSetup'));
const SkillsPage = lazy(() => import('./pages/Skills'));
const PricingPage = lazy(() => import('./pages/Pricing'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const BillingPage = lazy(() => import('./pages/Billing'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Handles scroll-to-top on route changes and hash-anchor scrolling.
 *
 * - Pathname change without hash → scroll to top
 * - Hash present (e.g. /#features) → wait for lazy content then scroll
 *   to the target element with smooth behavior
 */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // Retry with increasing delays to handle lazy-loaded content
      const delays = [0, 100, 300, 600];
      const timers: ReturnType<typeof setTimeout>[] = [];

      for (const delay of delays) {
        timers.push(
          setTimeout(() => {
            const el = document.getElementById(id);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth' });
              timers.forEach(clearTimeout);
            }
          }, delay)
        );
      }

      return () => timers.forEach(clearTimeout);
    }

    // Scroll to top when pathname changes (not on initial mount)
    if (pathname !== prevPathname.current) {
      window.scrollTo(0, 0);
      prevPathname.current = pathname;
    }
  }, [pathname, hash]);

  return null;
}

export function App() {
  usePageTracking();

  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollManager />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Public pages share Navbar + background + footer via PublicLayout */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Route>

        {/* All authenticated pages share AppLayout (header + background) */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/profile/setup" element={<ProfileSetupPage />} />
          <Route path="/onboard" element={<OnboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/agent" element={<AgentPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/skills" element={<SkillsPage />} />
          <Route path="/billing" element={<BillingPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
